import React, { useCallback, useEffect, useRef, useState } from "react";

// Util
import { Link } from "react-router-dom";
import AccessControl from "../ButtonComponents/AccessControl";
import {
  migrateProjectToBpmn,
  previewBpmnMigration,
} from "../../../../utils/Requests/Projects";

// MUI
import {
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

// MUI Icons

import FindReplaceIcon from "@mui/icons-material/FindReplace";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";
import ChecklistIcon from "@mui/icons-material/Checklist";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import {
  CheckCircleOutline,
  InsertDriveFile,
  PlayCircleOutline,
  Restore,
  Error,
  PublishOutlined,
  SaveOutlined,
  Send,
  Schedule,
  Security,
} from "@mui/icons-material";
import { CanvasHeaderButton } from "./CanvasStyledComponents";
import { tooltips } from "../../../../utils/CanvasUtil";
import CompareArrows from "@mui/icons-material/CompareArrows";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import FileLibraryModal from "../../../FileLibrary/FileLibraryModal";
import ProjectLogModal from "../../../pages/ProjectLog/ProjectLogModal";
import WorkflowSchedulesModal from "../../../pages/WorkflowSchedules/WorkflowSchedulesModal";
import ErrorHandlingModal from "../../../pages/ErrorHandling/ErrorHandlingModal";
import ManagedRunPolicyModal from "../../../pages/ManagedRunPolicy/ManagedRunPolicyModal";
import useProjectStore from "../../../ProjectCanvas/useProjectStore";
import { hasManagedRunsAccessForClient } from "../../../../constants/featureFlags";
// Width thresholds (in px) at which buttons collapse into the More menu.
// Buttons collapse right-to-left: Files first, then Sessions, Run.
// Publish, Save, and More are always visible.
const COLLAPSE_THRESHOLDS = [
  620, // below this: hide Files
  520, // below this: also hide Sessions
  420, // below this: also hide Run
];

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(Infinity);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

export default function MainCanvasButtons(props) {
  const {
    production,
    pauseSave,
    saving,
    isCanvasLocked,
    firePublish,
    updateState,
    anchorEl,
    saveDraft,
    saveDraftVersion,
    handleTest,
    publishedVersion,
    handleRenamePlaceholders,
    aggregate,
    error,
    handleAccess,
    rerenderBadge,
    setLoadingDialog,
    checkUnpublishedChanges,
    onDescriptionClick,
    useBpmnJoinBehavior = false,
  } = props;

  const [containerRef, containerWidth] = useContainerWidth();
  const clientId = useProjectStore((s) => s.clientId);
  const hasManagedRunsAccess = hasManagedRunsAccessForClient(clientId);

  const [fileLibraryOpen, setFileLibraryOpen] = useState(false);
  const [projectLogOpen, setProjectLogOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [errorHandlingOpen, setErrorHandlingOpen] = useState(false);
  const [managedRunPolicyOpen, setManagedRunPolicyOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  // Determine which collapsible buttons are visible based on container width.
  const showRun = containerWidth > COLLAPSE_THRESHOLDS[2];
  const showSessions = containerWidth > COLLAPSE_THRESHOLDS[1];
  const showFiles = containerWidth > COLLAPSE_THRESHOLDS[0];
  const managedRunsEnabled =
    aggregate?.extras?.managedRunPolicy?.enabled === true;

  // BPMN Migration state
  const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationError, setMigrationError] = useState(null);
  const [migrationSuccess, setMigrationSuccess] = useState(false);

  const handleOpenMigrateDialog = async () => {
    setMoreMenuAnchor(null);
    setMigrateDialogOpen(true);
    setMigrationLoading(true);
    setMigrationError(null);
    setMigrationSuccess(false);

    try {
      const response = await previewBpmnMigration(aggregate.project_id);
      if (response && response.data) {
        setMigrationPreview(response.data);
      }
    } catch (err) {
      setMigrationError(
        "Failed to preview migration: " + (err.message || "Unknown error")
      );
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleMigrate = async () => {
    setMigrationLoading(true);
    setMigrationError(null);

    try {
      const response = await migrateProjectToBpmn(aggregate.project_id);
      if (response && response.data && response.data.success) {
        setMigrationSuccess(true);
        // Reload the page to get the updated workflow
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setMigrationError(response?.data?.error || "Migration failed");
      }
    } catch (err) {
      setMigrationError(
        "Migration failed: " + (err.message || "Unknown error")
      );
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleCloseMigrateDialog = () => {
    if (!migrationLoading) {
      setMigrateDialogOpen(false);
      setMigrationPreview(null);
      setMigrationError(null);
      setMigrationSuccess(false);
    }
  };

  // Build overflow menu items for collapsed buttons
  const overflowItems = [];
  if (!showRun) {
    overflowItems.push({
      key: "run",
      label: "Run",
      icon: <PlayCircleOutline />,
      disabled: production || isCanvasLocked || publishedVersion === "None",
      onClick: () => {
        setMoreMenuAnchor(null);
        setLoadingDialog(true);
        checkUnpublishedChanges(true);
      },
    });
  }
  if (!showSessions) {
    overflowItems.push({
      key: "sessions",
      label: "Sessions",
      icon: <ChecklistIcon />,
      disabled: production || isCanvasLocked,
      onClick: () => {
        setMoreMenuAnchor(null);
        setProjectLogOpen(true);
      },
    });
  }
  if (!showFiles) {
    overflowItems.push({
      key: "files",
      label: "Files",
      icon: <InsertDriveFile />,
      disabled: production || isCanvasLocked,
      onClick: () => {
        setMoreMenuAnchor(null);
        setFileLibraryOpen(true);
      },
    });
  }
  return (
    <Grid
      ref={containerRef}
      container
      alignItems={"center"}
      spacing={1}
      wrap="nowrap"
    >
      {/* --- Always visible: Publish --- */}
      <Tooltip title={tooltips.publish}>
        <Grid item>
          <div>
            <CanvasHeaderButton
              disabled={production || pauseSave || saving || isCanvasLocked}
              variant={"outlined"}
              onClick={firePublish}
              className={"canvas-button-disabled"}
              startIcon={<Send />}
            >
              Publish
              <div className="atom-dot"></div>
            </CanvasHeaderButton>
          </div>
        </Grid>
      </Tooltip>

      {/* --- Always visible: Save --- */}
      <Tooltip title={tooltips.save} placement="top-end">
        <Grid item>
          <CanvasHeaderButton
            disabled={production || pauseSave || saving || isCanvasLocked}
            variant={"outlined"}
            className={"canvas-button-disabled"}
            onClick={(event) => updateState({ anchorEl: event.target })}
            startIcon={<SaveOutlined />}
          >
            Save
          </CanvasHeaderButton>
        </Grid>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => updateState({ anchorEl: null })}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <MenuItem
          onClick={() => {
            saveDraft();
            updateState({ anchorEl: null });
          }}
        >
          <SaveOutlined sx={{ mr: 1 }} /> Save
        </MenuItem>
        <MenuItem
          onClick={() => {
            saveDraftVersion();
            updateState({ anchorEl: null });
          }}
        >
          <BookmarkIcon sx={{ mr: 1 }} /> Save as version
        </MenuItem>
      </Menu>

      {/* --- Collapsible: Run --- */}
      {showRun && (
        <Tooltip title={tooltips.start}>
          <Grid item>
            <CanvasHeaderButton
              disabled={production || isCanvasLocked}
              variant={"outlined"}
              color={"primary"}
              className={publishedVersion === "None" && "disabled-btn"}
              onClick={() => {
                setLoadingDialog(true);
                checkUnpublishedChanges(true);
              }}
              startIcon={<PlayCircleOutline />}
            >
              Run
            </CanvasHeaderButton>
          </Grid>
        </Tooltip>
      )}

      {/* --- Collapsible: Sessions --- */}
      {showSessions && (
        <Tooltip title={tooltips.logs}>
          <Grid item>
            <CanvasHeaderButton
              disabled={production || isCanvasLocked}
              variant={"outlined"}
              startIcon={<ChecklistIcon />}
              onClick={() => setProjectLogOpen(true)}
            >
              Sessions
            </CanvasHeaderButton>
          </Grid>
        </Tooltip>
      )}
      <ProjectLogModal
        open={projectLogOpen}
        onClose={() => setProjectLogOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />

      {/* --- Collapsible: Files --- */}
      {showFiles && (
        <Tooltip title={tooltips.files}>
          <Grid item>
            <CanvasHeaderButton
              disabled={production || isCanvasLocked}
              variant={"outlined"}
              startIcon={<InsertDriveFile />}
              onClick={() => setFileLibraryOpen(true)}
            >
              Files
            </CanvasHeaderButton>
          </Grid>
        </Tooltip>
      )}
      <FileLibraryModal
        open={fileLibraryOpen}
        onClose={() => setFileLibraryOpen(false)}
        projectId={aggregate.project_id}
      />

      {/* --- Always visible: Access Control --- */}
      <Grid item>
        {aggregate.project_id && aggregate.components && (
          <AccessControl
            handleAccess={handleAccess}
            components={aggregate.components}
            projectId={aggregate.project_id}
            rerenderBadge={rerenderBadge}
            tooltips={tooltips}
            isCanvasLocked={isCanvasLocked}
          />
        )}
      </Grid>

      {managedRunsEnabled && (
        <Grid item>
          <CanvasHeaderButton variant={"outlined"} disabled>
            Managed run: On
          </CanvasHeaderButton>
        </Grid>
      )}

      {/* --- Always visible: More menu --- */}
      <Tooltip title="More options">
        <Grid item>
          <CanvasHeaderButton
            disabled={isCanvasLocked}
            variant={"outlined"}
            startIcon={<MoreVertIcon />}
            onClick={(event) => setMoreMenuAnchor(event.target)}
          >
            More
          </CanvasHeaderButton>
        </Grid>
      </Tooltip>
      <Menu
        id="more-options-menu"
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        MenuListProps={{ "aria-labelledby": "more-options-button" }}
      >
        {/* Overflow items from collapsed buttons */}
        {overflowItems.map((item) => (
          <MenuItem
            key={item.key}
            onClick={item.onClick}
            disabled={item.disabled}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        {overflowItems.length > 0 && <Divider />}

        <MenuItem
          onClick={() => {
            onDescriptionClick?.();
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <DescriptionIcon sx={{ mr: 1 }} /> Description
        </MenuItem>
        <Link
          className={"canvasLink"}
          to={`/project/version_history/${aggregate.project_id}`}
        >
          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
            }}
            disabled={production || isCanvasLocked}
          >
            <UpdateIcon sx={{ mr: 1 }} /> Version History
          </MenuItem>
        </Link>
        <Link
          className={"canvasLink"}
          to={`/project/workflow_schema/${aggregate.project_id}`}
        >
          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
            }}
            disabled={production || isCanvasLocked}
          >
            <CompareArrows sx={{ mr: 1 }} /> Compare Versions
          </MenuItem>
        </Link>
        <MenuItem
          onClick={() => {
            setSchedulesOpen(true);
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <Schedule sx={{ mr: 1 }} /> Schedules
        </MenuItem>
        <MenuItem
          onClick={() => {
            setErrorHandlingOpen(true);
            setMoreMenuAnchor(null);
          }}
          disabled={isCanvasLocked}
        >
          <Error sx={{ mr: 1 }} /> Error Handling
        </MenuItem>
        {hasManagedRunsAccess && (
          <MenuItem
            onClick={() => {
              setManagedRunPolicyOpen(true);
              setMoreMenuAnchor(null);
            }}
            disabled={isCanvasLocked}
          >
            <Security sx={{ mr: 1 }} /> Managed run
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleRenamePlaceholders(true);
            setMoreMenuAnchor(null);
          }}
          disabled={production || isCanvasLocked}
        >
          <FindReplaceIcon sx={{ mr: 1 }} /> Replace Placeholders
        </MenuItem>
        {!useBpmnJoinBehavior && (
          <MenuItem
            onClick={handleOpenMigrateDialog}
            disabled={production || isCanvasLocked}
          >
            <UpgradeIcon sx={{ mr: 1 }} /> Migrate to V2
          </MenuItem>
        )}
      </Menu>
      <WorkflowSchedulesModal
        open={schedulesOpen}
        onClose={() => setSchedulesOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />
      <ErrorHandlingModal
        open={errorHandlingOpen}
        onClose={() => setErrorHandlingOpen(false)}
        projectId={aggregate.project_id}
        projectName={aggregate.project_name}
      />
      {hasManagedRunsAccess && (
        <ManagedRunPolicyModal
          open={managedRunPolicyOpen}
          onClose={() => setManagedRunPolicyOpen(false)}
          projectId={aggregate.project_id}
          projectName={aggregate.project_name}
        />
      )}

      {/* BPMN Migration Dialog */}
      <Dialog
        open={migrateDialogOpen}
        onClose={handleCloseMigrateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Migrate to V2 Mode</DialogTitle>
        <DialogContent>
          {migrationLoading && !migrationSuccess && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress />
            </div>
          )}

          {migrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {migrationError}
            </Alert>
          )}

          {migrationSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Migration successful! Reloading workflow...
            </Alert>
          )}

          {!migrationLoading && !migrationSuccess && migrationPreview && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                This will migrate your workflow to use explicit V2 merge nodes
                (Merge Paths with "ALL" mode) for parallel paths.
              </DialogContentText>

              {migrationPreview.count > 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{migrationPreview.count}</strong> component(s) will
                  have a Merge Paths node inserted before them to maintain the
                  same behavior.
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  No structural changes needed. The workflow will be marked as
                  V2-enabled.
                </Alert>
              )}

              <DialogContentText variant="body2" color="text.secondary">
                A new version of the workflow will be created. You can revert to
                the previous version from Version History if needed.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseMigrateDialog}
            disabled={migrationLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMigrate}
            variant="contained"
            color="primary"
            disabled={migrationLoading || migrationSuccess || !migrationPreview}
          >
            {migrationLoading ? "Migrating..." : "Migrate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
